import { z } from 'zod';
import { RMM, SMIG } from '@shared/constants/Taux';
import { formatPayrollAmount } from '@shared/constants/PayrollParameters';
import { optionnel, optionnelNombre, DATE_ISO } from '@shared/validation/helpers';
import {
  ibanGabon,
  nifGabon,
  nomPersonne,
  numeroCnamgs,
  numeroCnss,
  permisTravailGabon,
  telephoneGabon,
  validerNumeroPiece,
} from '@shared/validation/identifiantsGabon';

/** Plafond de saisie salaire de base (évite montants absurdes / débordement UI). */
export const SALAIRE_BASE_MAX = 50_000_000;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function ageAt(birth: string, at: string): number {
  const [by, bm, bd] = birth.split('-').map(Number);
  const [ay, am, ad] = at.split('-').map(Number);
  let age = (ay ?? 0) - (by ?? 0);
  if ((am ?? 0) < (bm ?? 0) || ((am ?? 0) === (bm ?? 0) && (ad ?? 0) < (bd ?? 0))) age -= 1;
  return age;
}

const ouiNonBool = z
  .union([
    z.boolean(),
    z.literal('true'),
    z.literal('false'),
    z.literal('1'),
    z.literal('0'),
    z.literal('oui'),
    z.literal('non'),
  ])
  .transform((v) => v === true || v === 'true' || v === '1' || v === 'oui');

export const SalarieSchema = z
  .object({
    matricule: z.string().max(50),
    nom: nomPersonne,
    prenom: nomPersonne,
    poste: z
      .string()
      .trim()
      .min(1, 'Fonction requise')
      .max(120, '120 caractères maximum'),
    nom_jeune_fille: optionnel(nomPersonne),
    date_naissance: optionnel(
      z.string().regex(DATE_ISO, 'Date de naissance invalide (JJ/MM/AAAA)'),
    ),
    lieu_naissance: optionnel(z.string().max(120)),
    date_embauche: z.string().regex(DATE_ISO, 'Date embauche invalide (JJ/MM/AAAA)'),
    categorie: z.enum([
      'Direction',
      'Cadre superieur',
      'Cadre',
      'Agent de maitrise',
      'Employe qualifie',
      'Employe',
      'Ouvrier qualifie',
      'Ouvrier',
      'Stagiaire',
    ]),
    statut_contrat: z
      .enum(['CDI', 'CDD', 'essai', 'stage', 'apprentissage', 'interim'])
      .default('CDI'),
    date_fin_contrat: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    essai_fin: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    coefficient: optionnel(z.string().max(20)),
    service: optionnel(z.string().max(120)),
    manager: optionnel(z.string().max(120)),
    convention: optionnel(z.string().max(200)),
    statut: z.enum(['actif', 'inactif', 'conge', 'suspendu', 'sortie']).default('actif'),
    salaire_base: z
      .number({ error: 'Salaire de base : nombre entier requis' })
      .int('Salaire de base : nombre entier (sans décimales)')
      .positive('Salaire de base : entier positif requis')
      .max(
        SALAIRE_BASE_MAX,
        `Salaire de base trop élevé (max ${SALAIRE_BASE_MAX.toLocaleString('fr-FR')} FCFA)`,
      ),
    sursalaire: optionnelNombre(z.number().int().min(0).max(SALAIRE_BASE_MAX)),
    indemnite_transport: optionnelNombre(z.number().int().min(0).max(SALAIRE_BASE_MAX)),
    indemnite_vehicule: optionnelNombre(z.number().int().min(0).max(SALAIRE_BASE_MAX)),
    indemnite_logement: optionnelNombre(z.number().int().min(0).max(SALAIRE_BASE_MAX)),
    indemnite_fonction: optionnelNombre(z.number().int().min(0).max(SALAIRE_BASE_MAX)),
    indemnite_autres: optionnelNombre(z.number().int().min(0).max(SALAIRE_BASE_MAX)),
    nif: optionnel(nifGabon),
    numero_cnss: optionnel(numeroCnss),
    cnamgs: optionnel(numeroCnamgs),
    type_piece: optionnel(z.enum(['CNI', 'Passeport', 'Carte sejour'])),
    cni: optionnel(z.string().max(40)),
    piece_delivrance: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    piece_validite: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    piece_autorite: optionnel(z.string().max(160)),
    situation: optionnel(z.enum(['celibataire', 'marie', 'divorce', 'veuf', 'pacs'])),
    regime_matrimonial: optionnel(z.string().max(80)),
    date_mariage: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    enfants: optionnel(z.coerce.number().int().min(0).max(20)),
    personnes_charge: optionnel(z.coerce.number().int().min(0).max(10)),
    conjoint_actif: optionnel(ouiNonBool),
    date_deces_conjoint: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    conjoint_nom: optionnel(z.string().max(120)),
    conjoint_prenom: optionnel(z.string().max(120)),
    conjoint_nif: optionnel(z.string().max(20)),
    conjoint_profession: optionnel(z.string().max(120)),
    medecine_centre: optionnel(z.string().max(160)),
    medecin_referent: optionnel(z.string().max(120)),
    visite_derniere: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    visite_prochaine: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    aptitude: optionnel(
      z.enum(['apte', 'apte_restriction', 'inapte_temporaire', 'inapte_definitif']),
    ),
    groupe_sanguin: optionnel(z.string().max(4)),
    restrictions_medicales: optionnel(z.string().max(300)),
    vaccinations: optionnel(z.string().max(300)),
    sejour_num: optionnel(z.string().max(40)),
    sejour_type: optionnel(z.string().max(80)),
    sejour_delivrance: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    sejour_expiration: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    permis_travail: optionnel(permisTravailGabon),
    centre_cout: optionnel(z.string().max(80)),
    banque: optionnel(z.string().max(120)),
    agence: optionnel(z.string().max(120)),
    code_banque: optionnel(z.string().max(10)),
    code_guichet: optionnel(z.string().max(10)),
    num_compte: optionnel(z.string().max(30)),
    cle_rib: optionnel(z.string().max(5)),
    iban: optionnel(ibanGabon),
    bic: optionnel(z.string().max(20)),
    mobile_money: optionnel(z.string().max(30)),
    mode_paiement: optionnel(z.enum(['virement', 'cheque', 'especes', 'mobile_money'])),
    diplome: optionnel(z.string().max(160)),
    ecole: optionnel(z.string().max(160)),
    annee_diplome: optionnelNombre(z.number().int().min(1950).max(2030)),
    niveau_etudes: optionnel(z.string().max(80)),
    certifications: optionnel(z.string().max(300)),
    niveau_anglais: optionnel(z.string().max(40)),
    langues: optionnel(z.string().max(300)),
    contact_urgence_nom: optionnel(z.string().max(160)),
    contact_urgence_lien: optionnel(z.string().max(40)),
    contact_urgence_telephone: optionnel(telephoneGabon),
    contact_urgence_telephone_2: optionnel(telephoneGabon),
    contact_urgence_adresse: optionnel(z.string().max(200)),
    date_sortie: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    date_reintegration: optionnel(z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise')),
    motif_sortie: optionnel(z.string().max(80)),
    motif_detail: optionnel(z.string().max(300)),
    conges_solde: optionnelNombre(z.number().min(0)),
    email: z.string().min(1, 'Email requis').email('Adresse email invalide'),
    email_perso: optionnel(z.string().email('Adresse email invalide')),
    telephone: optionnel(telephoneGabon),
    telephone_secondaire: optionnel(telephoneGabon),
    sexe: optionnel(z.enum(['M', 'F'])),
    nationalite: optionnel(z.string().max(80)),
    adresse: optionnel(z.string().max(200)),
    ville: optionnel(z.string().max(80)),
    bp: optionnel(z.string().max(30)),
  })
  .superRefine((data, ctx) => {
    const today = todayIso();
    const dureeDeterminee =
      data.statut_contrat === 'CDD' ||
      data.statut_contrat === 'stage' ||
      data.statut_contrat === 'apprentissage' ||
      data.statut_contrat === 'interim';
    if (dureeDeterminee && !data.date_fin_contrat) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_fin_contrat'],
        message: 'Date de fin requise pour un contrat à durée déterminée',
      });
    }
    if (data.date_fin_contrat && data.date_fin_contrat < data.date_embauche) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_fin_contrat'],
        message: 'La date de fin précède la date d’embauche',
      });
    }
    if (data.date_embauche > today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_embauche'],
        message: 'La date d’embauche ne peut pas être dans le futur',
      });
    }
    if (data.date_sortie && data.date_sortie <= data.date_embauche) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_sortie'],
        message: 'La date de sortie doit être postérieure à la date d’embauche',
      });
    }
    if (data.date_reintegration && data.date_sortie && data.date_reintegration < data.date_sortie) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_reintegration'],
        message: 'La date de réintégration doit être postérieure ou égale à la date de sortie',
      });
    }
    const pieceErr = validerNumeroPiece(data.type_piece, data.cni);
    if (pieceErr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cni'],
        message: pieceErr,
      });
    }
    const nat = (data.nationalite ?? '').trim().toLowerCase();
    const estGabonais =
      !nat || nat === 'gabonaise' || nat === 'gabonais' || nat === 'ga' || nat.includes('gabon');
    if (!estGabonais && !data.permis_travail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['permis_travail'],
        message: 'Permis de travail obligatoire si nationalité ≠ gabonaise',
      });
    }
    if (data.date_naissance) {
      if (data.date_naissance > today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['date_naissance'],
          message: 'La date de naissance ne peut pas être dans le futur',
        });
      }
      if (data.date_naissance > data.date_embauche) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['date_naissance'],
          message: 'La naissance précède forcément l’embauche',
        });
      }
      if (ageAt(data.date_naissance, data.date_embauche) < 16) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['date_naissance'],
          message: 'Le salarié doit avoir au moins 16 ans à l’embauche',
        });
      }
    }
  });

/** True si le salaire de base est sous le SMIG (avertissement UI, pas un blocage schéma). */
export function salaireSousSmig(salaire: number): boolean {
  return salaire > 0 && salaire < SMIG;
}

/** Éléments fixes du salarié entrant dans le revenu mensuel (base + primes/indemnités obligatoires). */
export interface RevenuMensuelSalarie {
  salaire_base: number;
  sursalaire?: number;
  indemnite_transport?: number;
  indemnite_vehicule?: number;
  indemnite_logement?: number;
  indemnite_fonction?: number;
  indemnite_autres?: number;
}

/** Revenu mensuel total fixe : base + sursalaire + indemnités fixes (comparé au RMM). */
export function revenuMensuelTotal(s: RevenuMensuelSalarie): number {
  return (
    (s.salaire_base || 0) +
    (s.sursalaire || 0) +
    (s.indemnite_transport || 0) +
    (s.indemnite_vehicule || 0) +
    (s.indemnite_logement || 0) +
    (s.indemnite_fonction || 0) +
    (s.indemnite_autres || 0)
  );
}

/**
 * True si le revenu mensuel total est sous le RMM (150 000 FCFA : SMIG + primes obligatoires).
 * Avertissement UI uniquement, jamais un blocage.
 */
export function revenuSousRmm(s: RevenuMensuelSalarie): boolean {
  const total = revenuMensuelTotal(s);
  return total > 0 && total < RMM;
}

/** Messages construits depuis les constantes (aucun montant en dur). */
export function messageSalaireSousSmig(): string {
  return `Salaire de base sous le SMIG (${formatPayrollAmount(SMIG)}) — vérifiez la saisie`;
}

export function messageRevenuSousRmm(): string {
  return `Revenu mensuel total sous le revenu mensuel minimum (${formatPayrollAmount(RMM)}) — vérifiez les primes obligatoires`;
}

export type SalarieInput = z.infer<typeof SalarieSchema>;
